// Page "Facturation" (Lot 8.5.5b) — état de la souscription Hsasoft du
// tenant courant + actions liées (payer la BI pending, annuler, réactiver,
// souscrire).
//
// Backend exposé : GET/POST /api/billing/me, /start, /cancel (cf. billing.ts).
// Pas d'historique des BillingInvoices payées dans cette V1 — le backend ne
// l'expose pas encore. Reporté au Lot 8.6 si demandé.
//
// Réservé aux ADMIN du tenant : la facturation Hsasoft est une décision
// administrative (engagement financier, choix de plan). Les comptables
// peuvent voir l'état via la TrialBanner mais pas agir ici.
//
// Les 5 états affichés correspondent aux combinaisons possibles de
// (subscription, pending_billing_invoice, in_grace_period) renvoyées par
// /api/billing/me — voir `renderBillingState` ci-dessous.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "../api/client";
import {
  billingApi,
  billingInvoicesApi,
  type BillingInvoicePublic,
  type BillingMeResponse,
  type SubscriptionStatus,
} from "../api/billing";
import { api } from "../api/client";
import { useTranslation } from "react-i18next";
import { AppShell } from "../components/AppShell";
import { useAuthStore } from "../stores/auth";

// ---- Helpers ------------------------------------------------------------

function formatAmount(value: string, currency: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatCents(cents: number, currency: string): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function formatDateFR(iso: string): string {
  // Accepte ISO date (YYYY-MM-DD) ou ISO datetime — on suffixe T00:00:00Z
  // pour que le parsing reste UTC sans dérive de timezone côté navigateur.
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00Z" : ""));
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getStatusLabel(t: (k: string) => string) {
  return {
    incomplete: t("status.issued"),
    active: t("billingPage.planActive"),
    past_due: t("common.pending"),
    canceled: t("status.cancelled"),
  } as Record<string, string>;
}

const STATUS_BADGE: Record<SubscriptionStatus, string> = {
  incomplete: "bg-amber-100 text-amber-800",
  active: "bg-emerald-100 text-emerald-800",
  past_due: "bg-red-100 text-red-800",
  canceled: "bg-slate-200 text-slate-700",
};

// ---- Page ---------------------------------------------------------------

export function BillingPage() {
  const { t } = useTranslation();
  const currentRole = useAuthStore((s) => s.currentRole);
  const isAdmin = currentRole === "admin";

  const [data, setData] = useState<BillingMeResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [invoices, setInvoices] = useState<BillingInvoicePublic[]>([]);

  async function reload() {
    setLoadError(null);
    try {
      const me = await billingApi.me();
      setData(me);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : "Erreur de chargement";
      setLoadError(msg);
      setData(null);
    }
    // Charger l'historique en parallèle (sans bloquer l'affichage)
    try {
      const list = await billingInvoicesApi.list();
      setInvoices(list);
    } catch {
      // Non bloquant — l'historique est optionnel
    }
  }

  useEffect(() => {
    if (isAdmin) {
      void reload();
    }
  }, [isAdmin]);

  async function onCancelAtPeriodEnd() {
    const confirmed = globalThis.confirm(
      "Annuler la souscription à la fin de la période courante ? Vous garderez l'accès jusqu'au prochain renouvellement.",
    );
    if (!confirmed) return;
    setPending(true);
    setActionError(null);
    try {
      await billingApi.cancel();
      await reload();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : "Erreur lors de l'annulation";
      setActionError(msg);
    } finally {
      setPending(false);
    }
  }

  if (!isAdmin) {
    return (
      <AppShell>
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-900">{t("billingPage.title")}</h1>
        </header>
        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-md">
          Seuls les administrateurs peuvent gérer la souscription Hsasoft du
          tenant.
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">{t("billingPage.title")}</h1>
        <p className="text-sm text-slate-500">
          {t("billingPage.subtitle")}
        </p>
      </header>

      {loadError && (
        <div
          role="alert"
          className="mb-4 bg-red-50 border border-red-200 text-red-800 p-3 rounded-md text-sm"
        >
          {loadError}
        </div>
      )}

      {actionError && (
        <div
          role="alert"
          className="mb-4 bg-red-50 border border-red-200 text-red-800 p-3 rounded-md text-sm"
        >
          {actionError}
        </div>
      )}

      {data === null && !loadError && (
        <p className="text-sm text-slate-500">{t("common.loading")}</p>
      )}

      {data && <BillingStateCard data={data} actions={{ onCancelAtPeriodEnd, pending, invoices }} />}
    </AppShell>
  );
}

// ---- Rendu par état -----------------------------------------------------

function BillingStateCard({
  data,
  actions,
}: {
  data: BillingMeResponse;
  actions: { onCancelAtPeriodEnd: () => void; pending: boolean; invoices: BillingInvoicePublic[] };
}) {
  const { t } = useTranslation();
  const STATUS_LABEL = getStatusLabel(t);
  const { subscription, plan, pending_billing_invoice, in_grace_period } = data;
  const { invoices } = actions;

  // Cas 1 : pas de souscription (trial / free).
  if (subscription === null) {
    return (
      <section className="bg-white border border-slate-200 rounded-md p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-2">
          {t("billingPage.noSubscriptionTitle")}
        </h2>
        <p className="text-sm text-slate-600 mb-4">
          {t("billingPage.noSubscriptionDesc")}
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
        >
          {t("billingPage.choosePlan")}
        </Link>
      </section>
    );
  }

  return (
    <div className="space-y-4">
      {/* Bandeau d'état grâce / suspendu */}
      {subscription.status === "past_due" && (
        <div
          role="status"
          className={[
            "p-4 rounded-md border text-sm",
            in_grace_period
              ? "bg-amber-50 border-amber-200 text-amber-900"
              : "bg-red-50 border-red-200 text-red-900",
          ].join(" ")}
        >
          {in_grace_period
            ? t("billingPage.subtitle")
            : t("billingPage.subtitle")}
        </div>
      )}

      {/* Carte souscription */}
      <section className="bg-white border border-slate-200 rounded-md p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {plan?.name ?? "Plan"}
            </h2>
            <p className="text-sm text-slate-500">
              {t("billingPage.planExpires")} : {formatDateFR(subscription.current_period_start)} –{" "}
              {formatDateFR(subscription.current_period_end)}
            </p>
          </div>
          <span
            data-testid="subscription-status-badge"
            className={[
              "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold",
              STATUS_BADGE[subscription.status],
            ].join(" ")}
          >
            {STATUS_LABEL[subscription.status]}
          </span>
        </div>

        {plan && (
          <p className="text-sm text-slate-600">
            {t("common.currency")} :{" "}
            {formatCents(plan.price_monthly_cents, plan.currency)} {t("billingPage.perMonth")}
          </p>
        )}

        {/* Sous-état "annulation programmée" */}
        {subscription.cancel_at_period_end &&
          subscription.status !== "canceled" && (
            <p className="mt-3 text-sm text-slate-600">
              {t("billingPage.cancelScheduled")}{" "}
              <strong>
                {formatDateFR(subscription.current_period_end)}
              </strong>
              . Vous gardez l'accès jusqu'à cette date.
            </p>
          )}

        {/* Sous-état "annulée" */}
        {subscription.status === "canceled" && (
          <p className="mt-3 text-sm text-slate-600">
            Souscription terminée
            {subscription.canceled_at && (
              <> le {formatDateFR(subscription.canceled_at)}</>
            )}
            . Vous pouvez souscrire à nouveau quand vous voulez.
          </p>
        )}
      </section>

      {/* Carte BI pending → CTA Ziina */}
      {pending_billing_invoice && (
        <section className="bg-white border border-slate-200 rounded-md p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-2">
            Facture à régler
          </h2>
          <dl className="grid grid-cols-[140px_1fr] gap-y-1 text-sm text-slate-700 mb-4">
            <dt className="text-slate-500">Référence</dt>
            <dd className="font-mono">{pending_billing_invoice.reference}</dd>
            <dt className="text-slate-500">Période</dt>
            <dd>
              {formatDateFR(pending_billing_invoice.period_start)} –{" "}
              {formatDateFR(pending_billing_invoice.period_end)}
            </dd>
            <dt className="text-slate-500">Échéance</dt>
            <dd>{formatDateFR(pending_billing_invoice.due_date)}</dd>
            <dt className="text-slate-500">Montant</dt>
            <dd className="font-semibold">
              {formatAmount(
                pending_billing_invoice.amount_ttc,
                pending_billing_invoice.currency,
              )}
            </dd>
          </dl>
          {pending_billing_invoice.ziina_checkout_url ? (
            <a
              href={pending_billing_invoice.ziina_checkout_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
            >
              Payer maintenant
            </a>
          ) : (
            <p className="text-sm text-slate-500 italic">
              Lien de paiement en cours de génération — patientez quelques
              minutes puis rechargez la page.
            </p>
          )}
        </section>
      )}

      {/* Historique des factures Hsasoft */}
      {invoices.length > 0 && (
        <HistorySection invoices={invoices} />
      )}

      {/* Actions sur la souscription */}
      <section className="bg-white border border-slate-200 rounded-md p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-3">Actions</h2>
        {subscription.status === "active" &&
          !subscription.cancel_at_period_end && (
            <button
              type="button"
              onClick={actions.onCancelAtPeriodEnd}
              disabled={actions.pending}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {t("billingPage.cancelAtPeriodEnd")}
            </button>
          )}
        {subscription.cancel_at_period_end &&
          subscription.status !== "canceled" && (
            <p className="text-sm text-slate-500">
              Pour réactiver la souscription, contactez le support Hsasoft.
            </p>
          )}
        {subscription.status === "canceled" && (
          <Link
            to="/pricing"
            className="inline-flex items-center rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Souscrire à nouveau
          </Link>
        )}
        {subscription.status === "incomplete" && (
          <p className="text-sm text-slate-500">
            Finalisez le paiement de la première facture ci-dessus pour
            activer votre souscription.
          </p>
        )}
      </section>
    </div>
  );
}

// ---- Composant : historique des factures Hsasoft -------------------------

function getBiStatusLabel(t: (k: string) => string) {
  return { pending: t("common.pending"), paid: t("common.paid"), void: t("status.cancelled") } as Record<string, string>;
}

const BI_STATUS_CLS: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800",
  void: "bg-slate-100 text-slate-600",
};

function HistorySection({ invoices }: { invoices: BillingInvoicePublic[] }) {
  const { t } = useTranslation();
  const BI_STATUS_LABEL = getBiStatusLabel(t);
  return (
    <section className="bg-white border border-slate-200 rounded-md p-6">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">
        {t("billingPage.invoiceHistory")}
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wide">
              <th className="pb-2 pr-4">{t("common.reference")}</th>
              <th className="pb-2 pr-4">{t("common.date")}</th>
              <th className="pb-2 pr-4">{t("common.date")}</th>
              <th className="pb-2 pr-4">{t("common.amount")}</th>
              <th className="pb-2 pr-4">{t("common.status")}</th>
              <th className="pb-2">{t("billingPage.downloadPdf")}</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((bi) => (
              <tr key={bi.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 pr-4 font-mono text-slate-800">{bi.reference}</td>
                <td className="py-3 pr-4 text-slate-600">
                  {formatDateFR(bi.period_start)} – {formatDateFR(bi.period_end)}
                </td>
                <td className="py-3 pr-4 text-slate-600">{formatDateFR(bi.issued_at)}</td>
                <td className="py-3 pr-4 font-semibold text-slate-800">
                  {formatAmount(bi.amount_ttc, bi.currency)}
                </td>
                <td className="py-3 pr-4">
                  <span
                    className={[
                      "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
                      BI_STATUS_CLS[bi.status] ?? "bg-slate-100 text-slate-600",
                    ].join(" ")}
                  >
                    {BI_STATUS_LABEL[bi.status] ?? bi.status}
                  </span>
                </td>
                <td className="py-3">
                  <DownloadPdfButton billingInvoiceId={bi.id} reference={bi.reference} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---- Composant : bouton téléchargement PDF avec auth JWT -----------------

function DownloadPdfButton({
  billingInvoiceId,
  reference,
}: {
  billingInvoiceId: string;
  reference: string;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [dlErr, setDlErr] = useState<string | null>(null);

  async function handleDownload() {
    setBusy(true);
    setDlErr(null);
    try {
      const path = '/api/billing/invoices/' + billingInvoiceId + '/pdf';
      const { blob } = await api.getBlob(path);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = reference + '.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setDlErr(t('billingPage.downloadError'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span>
      <button
        type='button'
        onClick={() => void handleDownload()}
        disabled={busy}
        className='text-brand-600 hover:underline text-sm disabled:opacity-50'
      >
        {busy ? '…' : `↓ ${t('billingPage.downloadPdf')}`}
      </button>
      {dlErr && <span className='text-red-500 text-xs ml-1'>{dlErr}</span>}
    </span>
  );
}
